import { defineStore } from 'pinia'
import state from './state'
import getters from './getters'
import usePaginationStore from '../pagination'
import HaulageJobsApiRequest from '@/api/haulageJob'
import { HaulageJob, HaulageJobRequestForm, HaulageJobRequestFormCreate } from '@/models'
import StatusCodeType from '@/enum/StatusCodeType'
import CONSTANTS from '@/utils/constants/string-constants'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { markRaw } from 'vue'
import useGlobalStore from '../global'
import { fetchUntilExhaustion, pluralize } from '@/utils/helpers'
import NotificationType, { NotificationDuration } from '@/enum/NotificationType'
import {
  DEFAULT_PAGE_OFFSET,
  HAULAGE_JOBS_DETAILS_URL_NAME,
  HAULAGE_JOBS_EDIT_JOB_URL_NAME,
  HAULAGE_JOBS_LIST_STORE,
  MESSAGE_CONSTANTS,
  Network
} from '@/utils/constants'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import type { IHaulageJob } from '@/interfaces'
import HaulageJobFormCreate from '@/models/haulageJobs/HaulageJobFormCreate'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import moment from 'moment'
import ChangeStatus from '@/enum/ChangeStatus'
import { ExpandResponseType } from '@/enum'

const useHaulageJobsStore = defineStore('haulageJobsStore', {
  state: () => state,
  getters,
  actions: {
    async getHaulageJob(
      storeName: string,
      responseOptions?: Record<string, string | Record<string, string | string[]>>
    ) {
      const listStore = usePaginationStore(storeName)
      const haulageJobApi = new HaulageJobsApiRequest()
      responseOptions = {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.CUSTOMER},${ExpandResponseType.WASTE_COLLECTION_PROVIDER},${ExpandResponseType.WASTE_COLLECTION_SITE},${ExpandResponseType.WASTE_PRODUCT},
            ${ExpandResponseType.DELIVERY_AD_PLANT_SITE},${ExpandResponseType.WASTE_DELIVERY_SITE},${ExpandResponseType.WASTE_COLLECTION_SITE_INFO},${ExpandResponseType.DELIVERY_AD_PLANT_SITE_INFO},${ExpandResponseType.PURCHASE_ORDER_DOCUMENTS},
            ${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_COLLECTION_SITE},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_AD_PLANT_SITE},
            ${ExpandResponseType.WASTE_COLLECTION_PROVIDER_WASTE_DELIVERY_SITE},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_PRODUCT},${ExpandResponseType.PRICE},${ExpandResponseType.TOTAL_AMOUNT}`
      }
      listStore.handleListAPICall<HaulageJob>(
        haulageJobApi.getHaulageJob.bind(haulageJobApi),
        HaulageJob,
        responseOptions
      )
    },

    async getJobsForCalendar(start: string, end: string, options?: any) {
      const haulageJobApi = new HaulageJobsApiRequest()
      const {
        [ListFilterTypeExpression.SearchText]: searchText,
        [ListFilterTypeExpression.Filter]: filterOptions
      } = options ?? {}
      const startDate = moment(start).format('YYYY-MM-DD')
      const endDate = moment(end).format('YYYY-MM-DD')
      let responseOptions: any = {
        [ListResponseTypeExpression.Expand]: `${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.WASTE_COLLECTION_PROVIDER}`,
        [ListFilterTypeExpression.Filter]: {
          ...filterOptions,
          [CustomFilterTypeExpression.Expression]: `ScheduleDate>="${startDate}"&&ScheduleDate<="${endDate}"`
        }
      }
      if (searchText) {
        responseOptions = {
          ...responseOptions,
          [ListFilterTypeExpression.SearchText]: searchText
        }
      }
      const result = await fetchUntilExhaustion(
        haulageJobApi.getHaulageJob.bind(haulageJobApi),
        responseOptions
      )
      if (result.length) {
        const resData = result.map((job) => job).filter((job) => job.scheduleDate)
        return resData
      }
    },

    async getJobDetails(id: string) {
      this.isHaulageFormLoading = true
      try {
        const haulageJobApi = new HaulageJobsApiRequest()
        const result = await haulageJobApi.getHaulageJobById({
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.CUSTOMER},${ExpandResponseType.WASTE_COLLECTION_PROVIDER},${ExpandResponseType.WASTE_COLLECTION_SITE},${ExpandResponseType.WASTE_PRODUCT},
            ${ExpandResponseType.DELIVERY_AD_PLANT_SITE},${ExpandResponseType.WASTE_DELIVERY_SITE},${ExpandResponseType.WASTE_COLLECTION_SITE_INFO},${ExpandResponseType.DELIVERY_AD_PLANT_SITE_INFO},${ExpandResponseType.PURCHASE_ORDER_DOCUMENTS},
            ${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_COLLECTION_SITE},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_AD_PLANT_SITE},
            ${ExpandResponseType.WASTE_COLLECTION_PROVIDER_WASTE_DELIVERY_SITE},${ExpandResponseType.WASTE_COLLECTION_PROVIDER_PRODUCT},${ExpandResponseType.PRICE},${ExpandResponseType.TOTAL_AMOUNT}`
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.haulageJobDetails = new HaulageJob(result.data)
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isHaulageFormLoading = false
      }
    },

    async proccedToUpdateJobStatus(id: string, status?: number, currentStatus?: number, statusUpdate?: boolean) {
      if (id) this.isHaulageFormButtonLoading = true
      if (status != undefined) {
        this.haulageJobDetails.status = status
      }
      const result = await this.updateHaulageJob(id, statusUpdate)
      if (result.status === StatusCodeType.Success && result.data) {
        this.haulageJobDetails = new HaulageJob(result.data)
      } else {
        if (currentStatus != undefined) {
          this.haulageJobDetails.status = currentStatus
        }
      }
      this.isHaulageFormButtonLoading = false
      return result
    },

    async createHaulageJob(): Promise<{ status: StatusCodeType, data?: HaulageJob }> {
      const haulageJobApi = new HaulageJobsApiRequest()
      const haulageForm = markRaw(new HaulageJobRequestFormCreate(this.haulageJobForm))
      if (haulageForm.requestDates == null) {
        haulageForm.requestDates = []
      }
      const globalStore = useGlobalStore()
      const result = await haulageJobApi.addHaulageJob(haulageForm, {
        [ItemTypeExpression.Expand]: `${ExpandResponseType.CUSTOMER},${ExpandResponseType.WASTE_COLLECTION_PROVIDER},${ExpandResponseType.WASTE_COLLECTION_SITE},${ExpandResponseType.WASTE_PRODUCT},
        ${ExpandResponseType.WASTE_COLLECTION_SITE_INFO},${ExpandResponseType.DELIVERY_AD_PLANT_SITE},${ExpandResponseType.WASTE_DELIVERY_SITE},${ExpandResponseType.DELIVERY_AD_PLANT_SITE_INFO},${ExpandResponseType.PURCHASE_ORDER_DOCUMENTS},
        ${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT},${ExpandResponseType.PRICE},${ExpandResponseType.TOTAL_AMOUNT}`
      })
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`Haulage job`),
          message: `Haulage job ${CONSTANTS.CREATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      }
      return result
    },

    resetHaulageJobForm() {
      this.haulageJobForm = new HaulageJobFormCreate()
    },

    resetHaulageJobDetailsForm() {
      this.haulageJobDetails = new HaulageJob()
    },

    async updateHaulageJob(id: string, isStatusUpdate?: boolean): TApiResponse {
      const haulageJobApi = new HaulageJobsApiRequest()
      const haulageForm = markRaw(new HaulageJobRequestForm(this.haulageJobDetails as IHaulageJob))
      const globalStore = useGlobalStore()
      const result = await haulageJobApi.editHaulageJob(
        {
          id,
          [ItemTypeExpression.Expand]: `${ExpandResponseType.CUSTOMER},${ExpandResponseType.WASTE_COLLECTION_PROVIDER},${ExpandResponseType.WASTE_COLLECTION_SITE},${ExpandResponseType.WASTE_PRODUCT},${ExpandResponseType.WASTE_COLLECTION_SITE_INFO},${ExpandResponseType.PRICE},${ExpandResponseType.TOTAL_AMOUNT},
          ${ExpandResponseType.DELIVERY_AD_PLANT_SITE},${ExpandResponseType.WASTE_DELIVERY_SITE},${ExpandResponseType.DELIVERY_AD_PLANT_SITE_INFO},${ExpandResponseType.PURCHASE_ORDER_DOCUMENTS},${ExpandResponseType.ASSIGNED_EMPLOYEE},${ExpandResponseType.ASSIGNED_VEHICLE},${ExpandResponseType.ASSIGNED_EQUIPMENT}`
        },
        haulageForm
      )
      if (result.status === StatusCodeType.Success) {
        globalStore.addToastNotification({
          id: Symbol(`haulage`),
          message: `Haulage job ${isStatusUpdate ? CONSTANTS.STATUS_UPDATED_SUCCESSFULLY : CONSTANTS.UPDATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      } else {
        globalStore.addToastNotification({
          id: Symbol(`haulage job`),
          message: `Haulage job ${MESSAGE_CONSTANTS.FAILED_TO_UPDATE}`,
          type: NotificationType.Failed
        })
      }
      return result
    },

    async deleteHaulageJob(id?: string) {
      const haulageJobApi = new HaulageJobsApiRequest()
      const globalStore = useGlobalStore()
      const haulageListStore = usePaginationStore(HAULAGE_JOBS_LIST_STORE)
      try {
        const templatesToDeleteIds = id ? [id] : (haulageListStore.selectedItems ?? [])
        if (!templatesToDeleteIds.length) throw new Error(MESSAGE_CONSTANTS.JOBS_UNAVAILABLE)
        let queryParams
        if (
          id ||
          haulageListStore.someItemsSelected ||
          (haulageListStore.allItemsSelected && !haulageListStore.isSelectedAllSnackbarItems)
        ) {
          queryParams = { [DeleteTypeExpression.Expression]: { Id: templatesToDeleteIds } }
        }
        const response = await haulageListStore.handleListDeleteAPICall(
          haulageJobApi.deleteHaulageJob.bind(haulageJobApi),
          queryParams
        )
        if (
          response?.status === StatusCodeType.Success &&
          response.data.succeeded > 0 &&
          response.data.failed === 0
        ) {
          globalStore.addToastNotification({
            id: Symbol(`Job deleted`),
            message: `${pluralize(response.data.succeeded, 'Haulage job')} ${CONSTANTS.DELETED_SUCCESSFULLY}`,
            type: NotificationType.Success
          })
          return Promise.resolve(response.data)
        }
        if (response?.data && response.data.failed > 0) {
          globalStore.handleBulkOperationErrorsView(response, 'Haulage job')
          return Promise.resolve(response.data)
        }
        if (response?.data?.results.length) return Promise.reject(response.data)
        throw new TypeError(Network.TypeError.BulkOperationTypeMismatch)
      } catch (error: any) {
        ErrorHandlerService.handleAndCatchError(error, error)
      }
      return Promise.reject()
    },

    async initializeJobDetailsForm(jobData: any, navigator?: any) {
      const currentRouteName = navigator?.getCurrentRoute()?.name

      if (!navigator || currentRouteName == HAULAGE_JOBS_DETAILS_URL_NAME) {
        this.haulageJobDetails = new HaulageJob(jobData)
      }
      if (navigator && currentRouteName == HAULAGE_JOBS_EDIT_JOB_URL_NAME) {
        this.getJobDetails(jobData.id)
      }
    },

    async handleRealtimeJobUpdateOnDetails(jobData: any, navigator: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const currentRouteName = navigator.getCurrentRoute()?.name
      const isUserOnJobDetailsView =
        currentRouteName == HAULAGE_JOBS_DETAILS_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id
      const isUserOnJobEditView =
        currentRouteName == HAULAGE_JOBS_EDIT_JOB_URL_NAME &&
        jobData.id == navigator.getCurrentRoute()?.params?.id

      const hasJobDetailsStatusChanged = this.haulageJobDetails.status !== jobData.status
      const hasJobEditDetailsStatusChanged = this.haulageJobDetails.status !== jobData.status
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == jobData.updaterId
      const globalStore = useGlobalStore()
      if (
        !hasLoggedInUserUpdatedTheJob &&
        jobData.changeStatus == ChangeStatus.Deleted &&
        (isUserOnJobDetailsView || isUserOnJobEditView)
      ) {
        globalStore.disableButtonsForRealtime = true
        globalStore.isJobDeletedResponse = true
        return
      }

      // handle job update on job details view
      if (isUserOnJobDetailsView) {
        if (hasJobDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasJobDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeUpdateNotification(jobData, navigator)
        }
      }

      // handle job update on job edit view
      if (isUserOnJobEditView) {
        if (hasJobEditDetailsStatusChanged && hasLoggedInUserUpdatedTheJob) {
          return
        }

        if (hasJobEditDetailsStatusChanged && !hasLoggedInUserUpdatedTheJob) {
          this.showJobStatusChangeUpdateNotification(jobData, navigator)
        }

        if (!hasLoggedInUserUpdatedTheJob) {
          this.showJobDetailsChangeOnEditView()
        }
      }
    },

    async showJobStatusChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_STATUS_CHANGED),
        message: MESSAGE_CONSTANTS.JOB_STATUS_CHANGED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeUpdateNotification(updatedData: any, navigator: any) {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Infinite,
        callback: () => {
          globalStore.removeAllNotifications()
          this.initializeJobDetailsForm(updatedData, navigator)
        }
      })
    },

    async showJobDetailsChangeOnEditView() {
      const globalStore = useGlobalStore()
      globalStore.disableButtonsForRealtime = true
      globalStore.addToastNotification({
        id: Symbol(MESSAGE_CONSTANTS.JOB_EDIT_UPDATED),
        message: MESSAGE_CONSTANTS.JOB_EDIT_UPDATED,
        type: NotificationType.Info,
        timeout: NotificationDuration.Medium
      })
    },

    async handleRealtimeJobUpdateOnList(job: any) {
      const loggedInUserId = localStorage.getItem('userId')
      const hasLoggedInUserUpdatedTheJob = loggedInUserId == job.updaterId
      if (hasLoggedInUserUpdatedTheJob) return

      const isNewlyAddedJob = job.updatedAt === job.createdAt
      const isUpdatedJob = !isNewlyAddedJob
      const jobsListStore = usePaginationStore(HAULAGE_JOBS_LIST_STORE)
      const jobIndex = jobsListStore.data?.findIndex((item) => item.id === job.id)
      const hasNoFiltersApplied =
        jobsListStore.offset === DEFAULT_PAGE_OFFSET &&
        Object.entries(jobsListStore.filtersOptions)?.length === 0

      const isJobMatchingFilters = async () => {
        const haulageJobApi = new HaulageJobsApiRequest()
        if (jobsListStore.filtersOptions) {
          const lastOffset = jobsListStore.offset - jobsListStore.currentPageSize
          const lastLimit = jobsListStore.limit
          const responseOptions = jobsListStore.filtersOptions
          const res = await haulageJobApi.getHaulageJob({
            ...responseOptions,
            [ListPagingTypeExpression.Offset]: lastOffset,
            [ListPagingTypeExpression.Limit]: lastLimit
          })
          if (res.data.value && res.data.value.length > 0) {
            const jobExists = res.data.value.some((item: IHaulageJob) => item.id === job.id)
            if (jobExists) {
              return true
            }
          }
          return false
        }
        return true
      }
      if (isNewlyAddedJob) {
        if (hasNoFiltersApplied || (await isJobMatchingFilters())) {
          if (jobsListStore.data != null && jobsListStore.data.length != 0) {
            jobsListStore.data?.unshift(new HaulageJob(job))
          } else {
            this.getHaulageJob(HAULAGE_JOBS_LIST_STORE)
          }
        }
        return
      }

      if (!isUpdatedJob || !jobsListStore.data || jobIndex === undefined) {
        return
      }

      const shouldRemoveJob =
        job.changeStatus === ChangeStatus.Deleted ||
        (Object.entries(jobsListStore.filtersOptions).length > 0 && !(await isJobMatchingFilters()))

      const shouldAddJob =
        Object.entries(jobsListStore.filtersOptions).length > 0 &&
        (await isJobMatchingFilters()) &&
        jobIndex === -1

      const removeJobNotMatchingFilters = async () => {
        if (jobsListStore.data && jobIndex !== undefined && jobIndex !== -1) {
          jobsListStore.data.splice(jobIndex, 1)
          if (jobsListStore.data.length == 0) {
            jobsListStore.resetPaginationStateAndNotFilters()
            this.getHaulageJob(HAULAGE_JOBS_LIST_STORE)
          }
        }
      }

      if (shouldRemoveJob) {
        await removeJobNotMatchingFilters()
        return
      }

      if (shouldAddJob) {
        jobsListStore.data?.unshift(new HaulageJob(job))
        return
      }

      if (jobIndex == -1) return
      jobsListStore.data[jobIndex] = new HaulageJob(job)
    }
  }
})
export default useHaulageJobsStore
